import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchData } from 'services/queries';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import {
  DataGridPremium,
  GridToolbarQuickFilter,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton
} from '@mui/x-data-grid-premium';
import { MdVisibility, MdDownload } from 'react-icons/md';
import { formatCurrency } from 'utils/formatters';
import dayjs from 'dayjs';
import { PaymentStatusChip } from './PaymentStatusChip';
import { PAYMENT_ENDPOINTS, PAYMENT_STATUS } from './constants';
import { getDaysUntilDate } from 'utils/dateUtils';

// Custom toolbar for Payment Transactions
const CustomToolbar = () => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      py={1}
    >
      <Box color="secondary" display="flex" justifyContent="flex-end" alignItems="flex-start">
        <GridToolbarQuickFilter />
      </Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={3}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
      </Stack>
    </Stack>
  );
};

const PaymentTransactions = () => {
  const navigate = useNavigate();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0
  });

  // Fetch all payments directly (payment-centric view)
  const { data: payments = [], isLoading } = useFetchData(
    PAYMENT_ENDPOINTS.LIST,
    { staleTime: 5 * 60 * 1000 }
  );

  const columns = [
    {
      field: 'payment_id',
      headerName: 'Payment #',
      width: 100,
      type: 'number',
      groupable: false,
      aggregable: false,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || !params.value) return '-';
        return (
          <Typography variant="body2" fontWeight="bold">
            #{params.value}
          </Typography>
        );
      }
    },
    {
      field: 'sales_order_order_identifier',
      headerName: 'Order ID',
      width: 110,
      groupable: true,
      aggregable: false,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params) return null;
        return (
          <Typography variant="body2" fontWeight="medium">
            {params.value || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'sales_order_buyer_social_reason',
      headerName: 'Client',
      minWidth: 150,
      flex: 1,
      groupable: true,
      aggregable: false,
      cellClassName: 'v-center-cell'
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      cellClassName: 'v-center-cell',
      type: 'singleSelect',
      valueOptions: ['TO_BE_ISSUED', 'AWAITING_PAYMENT', 'PAID', 'CANCELLED'],
      groupable: true,
      aggregable: false,
      valueGetter: (_, row) => {
        // Return the effective_status for filtering/export
        return row.effective_status || row.status || '';
      },
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        const {
          status,
          effective_status,
          is_overdue,
          paid_late,
          days_late
        } = params.row;

        if (!status) return '-';

        return (
          <PaymentStatusChip
            status={status}
            effective_status={effective_status}
            is_overdue={is_overdue}
            paid_late={paid_late}
            days_late={days_late}
          />
        );
      }
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 120,
      type: 'date',
      groupable: false,
      aggregable: false,
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => {
        // Return Date object for sorting or null
        return row.due_date ? dayjs(row.due_date).toDate() : null;
      },
      renderCell: (params) => {
        if (!params || !params.row || !params.row.due_date) return '-';

        const date = dayjs(params.row.due_date).toDate();
        const isOverdue = params.row.is_overdue;

        return (
          <Typography
            variant="body2"
            color={isOverdue ? 'error' : 'text.primary'}
            fontWeight={isOverdue ? 'bold' : 'normal'}
          >
            {dayjs(date).format('DD/MM/YYYY')}
          </Typography>
        );
      }
    },
    {
      field: 'date',
      headerName: 'Payment Date',
      width: 120,
      type: 'date',
      groupable: false,
      aggregable: false,
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => {
        // Return Date object for sorting or null
        return row.date ? dayjs(row.date).toDate() : null;
      },
      renderCell: (params) => {
        if (!params || !params.row || !params.row.date) return '-';

        const date = dayjs(params.row.date).toDate();
        return (
          <Typography variant="body2">
            {dayjs(date).format('DD/MM/YYYY')}
          </Typography>
        );
      }
    },
    {
      field: 'usd_value',
      headerName: 'Amount (USD)',
      width: 120,
      type: 'number',
      groupable: false,
      aggregable: true,
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => parseFloat(row.usd_value || 0),
      renderCell: (params) => {
        if (!params || params.value == null) return '-';
        return formatCurrency(params.value, 'USD');
      }
    },
    {
      field: 'brl_value',
      headerName: 'Amount (BRL)',
      width: 120,
      type: 'number',
      groupable: false,
      aggregable: true,
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => parseFloat(row.brl_value || 0),
      renderCell: (params) => {
        if (!params || params.value == null) return '-';
        return formatCurrency(params.value, 'BRL');
      }
    },
    {
      field: 'days_late',
      headerName: 'Days Late/On Time',
      width: 140,
      type: 'number',
      groupable: false,
      aggregable: true,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        const { paid_late, days_late, status } = params.row;

        // Only show for PAID payments
        if (status !== 'PAID') return '-';

        if (paid_late && days_late > 0) {
          return (
            <Chip
              label={`${days_late} days late`}
              color="error"
              size="small"
              variant="outlined"
            />
          );
        } else {
          return (
            <Chip
              label="On time"
              color="success"
              size="small"
              variant="outlined"
            />
          );
        }
      }
    },
    {
      field: 'percentage',
      headerName: 'Percentage',
      width: 100,
      type: 'number',
      groupable: false,
      aggregable: true,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || params.value == null) return '-';
        return `${params.value}%`;
      }
    },
    {
      field: 'sales_order_payment_terms',
      headerName: 'Payment Terms',
      width: 150,
      groupable: true,
      aggregable: false,
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => {
        // Use abbreviation for filtering if available, otherwise use payment_terms
        return row.sales_order_payment_term_template_abbreviation || row.sales_order_payment_terms || '';
      },
      renderCell: (params) => {
        if (!params) return '-';
        const termName = params.row.sales_order_payment_term_template_name;
        const termAbbr = params.row.sales_order_payment_term_template_abbreviation;

        if (termAbbr) {
          return (
            <Tooltip title={termName || ''} arrow>
              <Typography variant="body2">{termAbbr}</Typography>
            </Tooltip>
          );
        }
        return params.row.sales_order_payment_terms || '-';
      }
    },
    {
      field: 'contract_number',
      headerName: 'Contract Number',
      width: 130,
      groupable: true,
      aggregable: false,
      cellClassName: 'v-center-cell'
    },
    {
      field: 'intermediary',
      headerName: 'Intermediary',
      width: 120,
      groupable: true,
      aggregable: false,
      cellClassName: 'v-center-cell'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      disableExport: true,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || !params.row) return null;

        const handleDownload = () => {
          if (params.row.file_url) {
            // Construct full URL if needed
            const fileUrl = params.row.file_url.startsWith('http')
              ? params.row.file_url
              : `${process.env.REACT_APP_API_BASE_URL}${params.row.file_url}`;
            window.open(fileUrl, '_blank');
          }
        };

        return (
          <Box display="flex" gap={0.5}>
            <Tooltip title="View Order">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click from firing
                  navigate(`/admin/payment/order/${params.row.sales_order}?paymentId=${params.row.payment_id}`);
                }}
                sx={{ color: 'primary.main' }}
              >
                <MdVisibility size={18} />
              </IconButton>
            </Tooltip>
            {params.row.file_url && (
              <Tooltip title="Download Receipt">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  sx={{ color: 'secondary.main' }}
                >
                  <MdDownload size={18} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  // Row coloring based on payment status
  const getRowClassName = (params) => {
    if (!params || !params.row) return '';

    // Priority 1: Overdue (red) - highest priority
    if (params.row.effective_status === 'OVERDUE') {
      return 'overdue-row';
    }

    // Priority 2: Fully Paid (green)
    if (params.row.status === PAYMENT_STATUS.PAID) {
      return 'paid-row';
    }

    // Priority 3 & 4: Check days until due date for unpaid payments
    const dueDate = params.row.due_date;
    const status = params.row.status;

    // Only apply date-based coloring for unpaid payments
    if (dueDate && status && status !== PAYMENT_STATUS.PAID && status !== PAYMENT_STATUS.CANCELLED) {
      const daysUntil = getDaysUntilDate(dueDate);

      if (daysUntil !== null && daysUntil >= 0) {
        // Orange: due in next 10 days (0-9 days)
        if (daysUntil < 10) {
          return 'urgent-row';
        }
        // Yellow: due in 10-20 days (10-19 days)
        if (daysUntil >= 10 && daysUntil < 20) {
          return 'warning-row';
        }
      }
    }

    return '';
  };

  return (
    <Card sx={{
      height: 'calc(100vh - 120px)', // Adjusted for better screen utilization
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{
        p: 2,
        flexGrow: 1,
        minHeight: 0,
        overflow: 'hidden', // Prevent overflow
        display: 'flex',
        flexDirection: 'column'
      }}>
          <DataGridPremium
            rows={payments}
            columns={columns}
            loading={isLoading}
            pagination
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowClassName={getRowClassName}
            initialState={{
              sorting: {
                sortModel: [{ field: 'payment_id', sort: 'desc' }]
              },
              aggregation: {
                model: {
                  usd_value: 'sum',
                  brl_value: 'sum'
                }
              }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            onRowClick={(params) => {
              // Navigate to order details with payment ID as query parameter
              navigate(`/admin/payment/order/${params.row.sales_order}?paymentId=${params.row.payment_id}`);
            }}
            slots={{
              toolbar: CustomToolbar
            }}
            sx={{
              height: '100%', // Ensure DataGrid uses full available height
              '& .v-center-cell': {
                display: 'flex',
                alignItems: 'center'
              },
              '& .MuiDataGrid-cell': {
                borderBottom: 'none'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.paper',
                borderBottom: 2,
                borderColor: 'divider'
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              },
              '& .overdue-row': {
                backgroundColor: '#FFF5F5', // Light red for overdue
                '&:hover': {
                  backgroundColor: '#FFE5E5 !important',
                }
              },
              '& .urgent-row': {
                backgroundColor: '#FFF4ED', // Light orange for due in next 10 days
                '&:hover': {
                  backgroundColor: '#FFE7D6 !important',
                }
              },
              '& .warning-row': {
                backgroundColor: '#FFFBEB', // Light yellow for due in 10-20 days
                '&:hover': {
                  backgroundColor: '#FEF3C7 !important',
                }
              },
              '& .paid-row': {
                backgroundColor: '#F0FDF4', // Light green for paid
                '&:hover': {
                  backgroundColor: '#E5F7ED !important',
                }
              },
              '& .MuiDataGrid-virtualScroller': {
                flex: 1 // Ensure the virtual scroller expands
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderColor: 'divider'
              }
            }}
          />
      </Box>
    </Card>
  );
};

export default PaymentTransactions;
